import { formatTimeOfDay, isoAttr } from "../../lib/datetime.ts";
import { useEffect, useState, type ReactNode } from "react";
import { ScreenScaffold, type ScreenState } from "../ScreenScaffold.tsx";
import { useIdentity, roleAtLeast } from "../../state/identity.tsx";
import { useTimeRange } from "../../state/time-range.tsx";
import { useDataQuality } from "../../state/data-quality.tsx";
import { LivePauseControl } from "../../chrome/LivePauseControl.tsx";
import { TimeRangeControl } from "../../chrome/TimeRangeControl.tsx";
import {
  useComplianceMapping,
  type ComplianceFramework,
  type ComplianceMappingResponse,
  type ComplianceWindow,
} from "./use-compliance.ts";
import { FrameworkPicker } from "./FrameworkPicker.tsx";
import { ReferenceTable } from "./ReferenceTable.tsx";
import { ExportControl } from "./ExportControl.tsx";
import { CoverageSummary } from "./CoverageSummary.tsx";
import { partitionReferences } from "./vocab.ts";
import "./compliance.css";

/**
 * The Compliance view (task 10.1; design C6, 12.2). It surfaces the control-plane-owned crosswalk
 * that relates the tenant's findings to one of four external frameworks, over ONE REST read, role
 * `viewer`:
 *
 * - `GET /api/v1/compliance/mapping?framework=…` → the chosen framework's references with a mapped
 *   finding count each, the underlying findings (with crosswalk confidence + rationale), an unmapped
 *   count and a coverage summary. The mapping is ADVISORY — it is derived at read time, never
 *   mutates a finding, and is never an authorization input (Requirement 2).
 *
 * The read is WINDOWED (it takes `from`/`to`) and framework-scoped, so the window is resolved from
 * the shared time-range and pausing freezes the query exactly as it freezes the other windowed
 * screens (design 12.2, FR-13 AC2). The mapping read drives the scaffold state.
 *
 * It handles every state a screen must (design 12.3), mirroring the Posture screen:
 * - restricted: the read is viewer-level; an unknown/below-viewer role sees the restricted state.
 * - tenant-blocked (task 10.2; Requirement 4.6): a viewer+ with an unresolvable tenant context
 *   (`useIdentity().tenant === null`) is hard-blocked via an `error` state, never a zero-state.
 * - loading / error / empty / ready as the mapping query resolves; a cold-core waking error defers
 *   to the shell's banner (kept "loading" under it so there is no contradiction).
 *
 * This task (10.3) adds the framework PICKER over the four frameworks, the per-reference
 * finding-count TABLE with a drill-down to each reference's underlying findings (with the crosswalk
 * confidence + rationale), and the COVERAGE summary. Task 10.4 reports the window's
 * `meta.data_quality` into the shared persistent strip (so a sparse/empty mapping reads as "no
 * data" distinctly from "nothing mapped", Requirement 4.4) and renders the server-filled advisory
 * disclaimer banner (Requirement 2.3). The export affordance + route registration (10.5) remain a
 * SEPARATE task that builds on this file; a clear seam is left for it. The view hardcodes no
 * framework reference catalog — the four frameworks are the generated enum and the references come
 * only from the server response (Requirement 4.5).
 */

/**
 * The default framework the view opens on. It is the FIRST of the generated
 * {@link ComplianceFramework} enum, not a client-authored catalog; the picker (below) lets the
 * operator switch, and the hook re-queries on the chosen framework.
 */
const DEFAULT_FRAMEWORK: ComplianceFramework = "mitre_atlas";

export function ComplianceScreen(): ReactNode {
  const { role, tenant } = useIdentity();
  const { live } = useTimeRange();

  // The framework picker drives this state over the four generated frameworks (task 10.3); the hook
  // keys on `framework`, so selecting a different one issues a fresh query.
  const [framework, setFramework] = useState<ComplianceFramework>(DEFAULT_FRAMEWORK);

  const { query: mapping, window } = useComplianceMapping(framework);

  const state = resolveState(role, tenant, mapping);

  return (
    <ScreenScaffold
      title="Compliance"
      subtitle="Your current findings mapped to an external framework's references, with a per-reference count, a coverage summary, and the crosswalk rationale behind each mapping. Advisory only — not a claim of certification or audited compliance."
      state={state}
    >
      {mapping.data !== undefined && (
        <ComplianceBody
          mapping={mapping.data}
          window={window}
          live={live}
          framework={framework}
          onSelectFramework={setFramework}
        />
      )}
    </ScreenScaffold>
  );
}

/**
 * Decide which scaffold state to show from the role, the resolved tenant, and the mapping query
 * status.
 *
 * PRECEDENCE (task 10.2; Requirement 4.6): two gates sit AHEAD of the loading/empty/ready
 * resolution, in this order:
 *
 * 1. `restricted` — role gate first. An unknown or below-viewer role is a role problem, not a
 *    tenant problem; least privilege wants it reported as "not available for your role" and the
 *    tenant is irrelevant to a role that may not see the view at all. This matches the design
 *    ordering ("restricted when role < viewer; tenant-blocked when the operator has no resolved
 *    tenant context").
 * 2. tenant block — a viewer+ with an UNRESOLVABLE tenant (`tenant === null`) is HARD-BLOCKED.
 *    ScreenScaffold has no dedicated "tenant-blocked" kind; its hard blocks are `error` and
 *    `restricted`, and neither renders a body. We use `error` so the operator sees a failure, not a
 *    "role" message (their role is fine) and not a zero-state. Requirement 4.6 forbids falling
 *    through to an empty/zero-state render for an unresolvable tenant, so this gate is placed BEFORE
 *    the loading/empty/ready resolution below.
 *
 * The server enforces tenancy regardless — the gateway binds the tenant from the signed envelope —
 * so this block is a UX guard, not the authority.
 */
function resolveState(
  role: ReturnType<typeof useIdentity>["role"],
  tenant: ReturnType<typeof useIdentity>["tenant"],
  mapping: ReturnType<typeof useComplianceMapping>["query"],
): ScreenState {
  // Least privilege: an unknown or below-viewer role sees the restricted state, not an error.
  if (!roleAtLeast(role, "viewer")) {
    return {
      kind: "restricted",
      reason:
        role === "unknown"
          ? "Your role has not been resolved yet, so the compliance view is hidden until it is. The server enforces access regardless."
          : "The compliance view needs at least the viewer role.",
    };
  }

  // Tenant gate (Requirement 4.6): a viewer+ with no resolved tenant context is blocked entirely.
  // This is placed before the query resolution so an unresolvable tenant can never fall through to
  // an empty/zero-state render.
  if (tenant === null) {
    return {
      kind: "error",
      message:
        "Your account has no resolved tenant, so there is no estate to map to a framework. This is not an empty result — contact your administrator to confirm your tenant assignment. The server enforces tenant access regardless.",
    };
  }

  if (mapping.data !== undefined) {
    if (isMappingEmpty(mapping.data)) {
      return {
        kind: "empty",
        cause:
          "No findings mapped to this framework in this window, and nothing was left unmapped either. Widen the time range, or check that the AIS is reporting findings.",
      };
    }
    return { kind: "ready" };
  }

  if (mapping.isError) {
    // The cold-core waking error is the shell's banner; keep "loading" under it (no contradiction).
    if (mapping.error.isWaking) return { kind: "loading" };
    return { kind: "error", message: errorMessage(mapping.error) };
  }

  return { kind: "loading" };
}

function errorMessage(error: ReturnType<typeof useComplianceMapping>["query"]["error"]): string {
  if (error === null) return "The compliance view could not load.";
  switch (error.kind) {
    case "unauthorized":
      return "Your session has expired. Reload to sign in again.";
    case "forbidden":
      return "You are not permitted to see compliance data. The server enforces access.";
    case "network":
      return "The compliance request could not be sent. Check your connection.";
    default:
      return error.message;
  }
}

/**
 * True when the mapping has nothing to show for the window: no reference carries a mapped finding
 * AND nothing was left unmapped. This is the "no data" case (an empty window), kept distinct from
 * "nothing mapped" (findings exist but none crosswalk to a reference, i.e. `unmapped.count > 0`
 * with zero covered references) — the latter resolves to a READY view, and task 10.4 reports the
 * window's `meta.data_quality` facts into the shared strip so the two read apart (an empty window
 * shows honest-zero DQ facts; a "nothing mapped" window surfaces its drops/sampling/gaps). The
 * framework always declares its full reference set, so a non-zero `references.length` alone is not
 * evidence of data.
 */
function isMappingEmpty(mapping: ComplianceMappingResponse): boolean {
  const anyMapped = mapping.references.some((ref) => ref.finding_count > 0);
  return !anyMapped && mapping.unmapped.count === 0;
}

/**
 * The ready body (task 10.3). It composes the Compliance view's surfaces over the one mapping read:
 *
 * - the FRAMEWORK PICKER over the four generated frameworks (Requirement 4.1) — selecting one
 *   re-queries via the `framework` state the screen owns;
 * - the window/meta row (mirroring the Posture screen) plus the resolved framework + edition read
 *   from the response;
 * - the COVERAGE summary (references covered vs uncovered + the unmapped count — Requirement 4.3);
 * - the per-reference finding-count TABLE with a drill-down to each reference's underlying findings,
 *   showing their severity/tier, signal, and the crosswalk confidence + rationale (Requirements
 *   4.1, 4.2).
 *
 * It holds no client-side framework reference catalog: every reference, title, count and finding is
 * read from the response (Requirement 4.5). The export affordance (task 10.5) remains a seam.
 *
 * Task 10.4 wires two things through this body:
 *
 * - the DATA-QUALITY facts (Requirement 4.4): the window's `meta.data_quality` is reported into the
 *   shared persistent strip the same way the Overview/System/Assurance screens do — a `useEffect`
 *   keyed on the facts calls `report(...)`. This is what lets a SPARSE/EMPTY window read as "no
 *   data" DISTINCTLY from "nothing mapped": the strip always says whether sources went silent,
 *   whether anything was dropped and whether sampling is in effect, so an empty mapping with silent
 *   sources ("no data") reads apart from an empty mapping over healthy sources, and a "nothing
 *   mapped" window (findings exist, none crosswalk — a ready view) carries its own honest facts.
 * - the ADVISORY disclaimer (Requirement 2.3): a banner rendered from the response `advisory` field
 *   at the seam between the meta row and the mapping surfaces. The text is NOT hardcoded — it is the
 *   server-filled canonical constant, so the UI and the export carry identical wording.
 */
function ComplianceBody(props: {
  mapping: ComplianceMappingResponse;
  window: ComplianceWindow;
  live: boolean;
  framework: ComplianceFramework;
  onSelectFramework: (framework: ComplianceFramework) => void;
}): ReactNode {
  const { mapping, window } = props;
  const generatedAt = mapping.meta.generated_at;
  const { covered } = partitionReferences(mapping);

  // Keep the persistent data-quality strip consistent with this window (design 12.2, NFR-4), the
  // same way the Overview/System/Assurance screens do — so a sparse/empty mapping reads as "no
  // data" distinctly from "nothing mapped" (Requirement 4.4). Only the fields the response's
  // `meta.data_quality` carries are reported; `report` is a stable callback and the facts are plain
  // numbers/booleans, so this re-reports only when a fact actually changes.
  const { report } = useDataQuality();
  const dq = mapping.meta.data_quality;
  const gapCount = (dq.gaps ?? []).length;
  useEffect(() => {
    report({
      drops: { atSource: 0, atEdge: 0, inPipeline: dq.drops_in_range },
      sampling: { active: dq.sampling_in_effect, rate: null, exact: !dq.sampling_in_effect },
      hasSequenceGap: gapCount > 0,
    });
  }, [report, dq.drops_in_range, dq.sampling_in_effect, gapCount]);

  return (
    <div className="cmp" data-live={props.live}>
      <FrameworkPicker selected={props.framework} onSelect={props.onSelectFramework} />

      <div className="screen-meta-row">
        <LivePauseControl />
        <TimeRangeControl />
        <p className="cmp__meta">
          {props.live ? "Live" : "Paused"} · window{" "}
          <time dateTime={isoAttr(window.fromMs)}>{formatTimeOfDay(window.fromMs)}</time> to{" "}
          <time dateTime={isoAttr(window.toMs)}>{formatTimeOfDay(window.toMs)}</time> · generated{" "}
          <time dateTime={isoAttr(generatedAt)}>{formatTimeOfDay(generatedAt)}</time>
        </p>
      </div>

      {/* The advisory disclaimer (task 10.4; Requirement 2.3). It renders the server-filled
          `advisory` text verbatim — the UI holds no copy of the wording, so it stays identical to
          the export's disclaimer (the canonical constant). The data-quality facts for this window
          are reported into the shared persistent strip above (see the effect in ComplianceBody),
          not re-drawn here. */}
      {mapping.advisory.length > 0 && (
        <p className="cmp__advisory" role="note">
          {mapping.advisory}
        </p>
      )}

      <div
        className="cmp__panel"
        id="cmp-framework-panel"
        role="tabpanel"
        aria-labelledby={`cmp-framework-tab-${mapping.framework.id}`}
      >
        <p className="cmp__framework" aria-live="polite">
          {mapping.framework.id} · edition {mapping.framework.edition} ·{" "}
          {covered.length} reference{covered.length === 1 ? "" : "s"} with findings
        </p>

        <CoverageSummary mapping={mapping} />

        {/* The CSV/JSON export affordance (task 10.5; Requirements 4.5, 5.1). It downloads the
            CURRENT framework + window via the viewer-gated `/compliance/export`, over the same
            authenticated same-origin path the view used (the BFF proxies with the operator's
            identity). It serializes only what this view already shows (Requirement 5.5). */}
        <div className="cmp-export-row">
          <ExportControl framework={props.framework} window={window} />
        </div>

        <ReferenceTable references={mapping.references} />
      </div>
    </div>
  );
}
